import {
  cloneElement,
  type HTMLAttributes,
  isValidElement,
  type JSX,
  type ReactElement,
  type ReactNode,
  useEffect,
  useRef,
} from 'react';
import { OverlayPanel, type OverlayPanelProps } from 'primereact/overlaypanel';
import type { OverlayPanel as OverlayPanelType } from 'primereact/overlaypanel';
import { Button } from '../Button';
import { Icon } from '../Icon';
import styles from './Popover.module.scss';

type OverlayPanelWithRef = OverlayPanelType & {
  overlay?: HTMLElement;
  container?: HTMLElement;
  overlayRef?: { current: HTMLElement | null };
};

/**
 * Position defines the positioning of the popover relative to the trigger element.
 */
export type Position =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right'
  | 'left'
  | 'right';

/**
 * PopoverProps defines the properties for the Popover component.
 */
interface PopoverProps {
  id: string;
  triggerElement?: ReactNode;
  kebabMenu?: boolean;
  positioning: Position;
  showHeader?: boolean;
  header?: ReactNode;
  content: ReactNode;
  showActions?: boolean;
  actions?: ReactNode;
  popoverCustomClass?: string;
  showCloseIcon?: boolean;
  useFixedPosition?: boolean;
  overlayPanelProps?: Partial<OverlayPanelProps>;
}

// helper to identify what kind of element the trigger is
const getTriggerType = (trigger: ReactNode): 'icon' | 'button' | 'text' | 'unknown' => {
  if (!isValidElement(trigger)) {
    if (typeof trigger === 'string' || typeof trigger === 'number') return 'text';
    return 'unknown';
  }

  const element = trigger as ReactElement;

  if (element.type === Icon) return 'icon';
  if (element.type === Button) return 'button';

  if (typeof element.type === 'string') return 'text';

  return 'unknown';
};

/**
 * Custom Popover component using PrimeReact's OverlayPanel. It provides optional pointer,
 * flexible positioning around a trigger element, and structured sections for header,
 * content, and actions.
 *
 * ## Import path
 * ```tsx
 * import { Popover } from '@kpmg-global-audit-emu/react-ui-kit/components/Popover';
 * ```
 * @param root0 - The props object for the Popover component.
 * @param root0.id - Unique ID for the Popover.
 * @param root0.triggerElement - ReactNode element rendered as the trigger element.
 * @param root0.kebabMenu - Whether to show the caret pointer.
 * @param root0.positioning - Defines the popover's position relative to the trigger ('top' | 'bottom' | 'left' | 'right').
 * @param root0.showHeader - Whether to display the header section.
 * @param root0.header - ReactNode element rendered in the header.
 * @param root0.content - The main content of the popover.
 * @param root0.showActions - Whether to display the action section.
 * @param root0.actions - ReactNode elements rendered as popover actions.
 * @param root0.popoverCustomClass - Custom class for the popover.
 * @param root0.showCloseIcon - Whether to show the close icon.
 * @param root0.useFixedPosition - Whether to use fixed positioning for the popover.
 * @param root0.overlayPanelProps - Additional props to pass to the underlying PrimeReact OverlayPanel component.
 * @returns {JSX.Element} The Popover component.
 */
export const Popover = ({
  id,
  triggerElement,
  kebabMenu,
  positioning,
  showHeader,
  header,
  content,
  showActions,
  actions,
  popoverCustomClass,
  showCloseIcon,
  useFixedPosition = false,
  overlayPanelProps,
}: PopoverProps): JSX.Element => {
  const overlayPanelRef = useRef<OverlayPanelWithRef | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);

  // Disable PrimeReact's built-in align logic
  useEffect(() => {
    if (overlayPanelRef.current) {
      overlayPanelRef.current.align = () => {
        // intentionally empty to suppress default alignment
      };
    }
  }, []);

  const openPopover = (
    e: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLElement>,
    id: string
  ) => {
    const target = e.currentTarget;

    overlayPanelRef.current?.toggle(e);
    target.classList.add(styles['active-trigger']);

    const gap = kebabMenu === false ? 6 : 7;

    const getOverlayElement = (): HTMLElement | null => {
      const ref = overlayPanelRef.current;
      return (
        ref?.overlay ||
        ref?.container ||
        ref?.overlayRef?.current ||
        document.getElementById(id) ||
        document.querySelector('.p-overlaypanel')
      );
    };

    setTimeout(() => {
      const overlayEl = getOverlayElement();
      if (!overlayEl) return;

      if (useFixedPosition) {
        overlayEl.style.position = 'fixed';
      }

      // Hidden initially to avoid the jump
      overlayEl.style.visibility = 'hidden';
      overlayEl.style.opacity = '0';

      // ResizeObserver to wait for stable height/width

      const observer = new ResizeObserver(([entry]) => {
        const { height, width } = entry.contentRect;
        const triggerRect = target.getBoundingClientRect();

        const scrollX = useFixedPosition ? 0 : window.scrollX || window.pageXOffset;
        const scrollY = useFixedPosition ? 0 : window.scrollY || window.pageYOffset;

        let top = 0,
          left = 0;

        switch (positioning) {
          case 'top-left':
            top = scrollY + triggerRect.top - height - gap;
            left = scrollX + triggerRect.left;
            break;

          case 'top-center':
            top = scrollY + triggerRect.top - height - gap;
            left = scrollX + triggerRect.left + triggerRect.width / 2 - width / 2;
            break;

          case 'top-right':
            top = scrollY + triggerRect.top - height - gap;
            left = scrollX + triggerRect.right - width;
            break;

          case 'bottom-left':
            top = scrollY + triggerRect.bottom + gap;
            left = scrollX + triggerRect.left;
            break;

          case 'bottom-center':
            top = scrollY + triggerRect.bottom + gap;
            left = scrollX + triggerRect.left + triggerRect.width / 2 - width / 2;
            break;

          case 'bottom-right':
            top = scrollY + triggerRect.bottom + gap;
            left = scrollX + triggerRect.right - width;
            break;

          case 'left':
            top = scrollY + triggerRect.top + triggerRect.height / 2 - height / 2;
            left = scrollX + triggerRect.left - width - gap;
            break;

          case 'right':
            top = scrollY + triggerRect.top + triggerRect.height / 2 - height / 2;
            left = scrollX + triggerRect.right + gap;
            break;
        }

        overlayEl.style.top = `${top}px`;
        overlayEl.style.left = `${left}px`;

        // Fade in smoothly after positioned
        overlayEl.style.visibility = 'visible';
        overlayEl.style.opacity = '1';
        overlayEl.style.transition = 'opacity 0.15s ease-in-out';

        observer.disconnect();
      });

      observer.observe(overlayEl);
    }, 50);
  };
  useEffect(() => {
    const removeActive = () => {
      document.querySelectorAll(`.${styles['active-trigger']}`).forEach((el) => {
        el.classList.remove(styles['active-trigger']);
      });
    };

    const onDocumentClick = (e: MouseEvent) => {
      const overlayEl = document.querySelector('.p-overlaypanel') as HTMLElement | null;
      const clickedInsideOverlay = overlayEl ? overlayEl.contains(e.target as Node) : false;
      const clickedInsideTrigger = triggerRef.current
        ? triggerRef.current.contains(e.target as Node)
        : false;

      // if clicked outside both overlay and trigger -> remove class
      if (!clickedInsideOverlay && !clickedInsideTrigger) {
        removeActive();
      }
    };

    const onDocumentKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') removeActive();
    };

    document.addEventListener('click', onDocumentClick, true); // use capture to catch early
    document.addEventListener('keydown', onDocumentKey);

    return () => {
      document.removeEventListener('click', onDocumentClick, true);
      document.removeEventListener('keydown', onDocumentKey);
    };
  }, []);

  // Render the trigger element and automatically attach the click handler to the provided trigger
  const renderTrigger = (): JSX.Element => {
    // check for valid React element
    if (isValidElement(triggerElement)) {
      const element = triggerElement as ReactElement<Partial<HTMLAttributes<HTMLElement>>>;

      // Native HTML element (div, span, button, etc.)
      if (typeof element.type === 'string') {
        return cloneElement(element, {
          ...element.props,
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            element.props.onClick?.(e);
            openPopover(e, id);
          },
          role: element.props.role ?? 'button',
          tabIndex: element.props.tabIndex ?? 0,
          style: { cursor: 'pointer', ...element.props.style },
          onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openPopover(e as unknown as React.MouseEvent<HTMLElement>, id);
            }
            element.props.onKeyDown?.(e);
          },
        });
      }
    }

    // Non-element ReactNode (text, fragment, etc.)
    if (triggerElement) {
      return (
        <div
          role="button"
          tabIndex={0}
          onClick={(e) => openPopover(e, id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openPopover(e as unknown as React.MouseEvent<HTMLElement>, id);
            }
          }}
        >
          {triggerElement}
        </div>
      );
    }

    // Default fallback
    return (
      <Button
        buttonPrefix
        buttonType="Tertiary"
        classType="Standard"
        onClick={(e) => openPopover(e, id)}
        prefixInstance="Reports"
        size="Small"
        state="Default"
        theme="Rectangular"
      />
    );
  };

  const triggerType = getTriggerType(triggerElement);

  return (
    <div className={`${styles.popover}`}>
      <div
        className={`${styles['trigger-element']} ${kebabMenu === false || kebabMenu === undefined ? '' : styles['hide-pointer']}`}
        ref={triggerRef}
      >
        {renderTrigger()}
      </div>
      <OverlayPanel
        {...overlayPanelProps}
        ref={overlayPanelRef}
        id={id}
        showCloseIcon={overlayPanelProps?.showCloseIcon ?? true}
        className={`${styles['overlay-panel']} ${kebabMenu === false || kebabMenu === undefined ? styles.pointer : styles['hide-pointer']
          } ${styles[positioning]} ${popoverCustomClass ?? ''} ${triggerType === 'icon' ? styles.icon : ''} ${showCloseIcon ? '' : styles['hide-close']} ${overlayPanelProps?.className ?? ''}`.trim()}
        closeIcon={
          overlayPanelProps?.closeIcon ??
          ((overlayPanelProps?.showCloseIcon ?? showCloseIcon) ? (
            <Icon name="Close" shape="Square" size="X-Small" type="Base Icon" variant="Outline" />
          ) : null)
        }
        data-position={positioning}
        onHide={() => {
          // Remove active-trigger class when popover hides
          document
            .querySelectorAll(`.${styles['active-trigger']}`)
            .forEach((el) => el.classList.remove(styles['active-trigger']));
          overlayPanelProps?.onHide?.();
        }}
      >
        {(kebabMenu === false || kebabMenu === undefined) && (
          <div className={styles['popover-header']}>{showHeader && header}</div>
        )}
        <div className={styles['popover-content']}>{content}</div>
        {(kebabMenu === false || kebabMenu === undefined) && (
          <div className={styles['popover-actions']}>{showActions && actions}</div>
        )}
      </OverlayPanel>
    </div>
  );
};
